// Informe de clima de obra en PDF (jsPDF, todo vectorial: liviano y nítido). Sirve para justificar demoras o
// pedir ampliación de plazo ante el comitente: resumen, gráficos, días hábiles no aptos con su causa, tabla diaria,
// criterios y fuente de los datos, con el logo del cliente si hay. Fuente estándar (WinAnsi): sin emojis ni "≥".
import { jsPDF } from 'jspdf';

const VERDE = [43, 232, 139], INK = [16, 21, 18], MUT = [94, 107, 100], LINEA = [227, 232, 229], CARD = [243, 246, 244];
const EST = { apto: { txt: 'Apto', rgb: [16, 185, 129], suave: [209, 250, 229] }, cuidado: { txt: 'Con cuidado', rgb: [217, 119, 6], suave: [254, 243, 199] }, no: { txt: 'No apto', rgb: [225, 29, 72], suave: [255, 228, 230] } };
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const DOW = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
const n1 = (v) => (v == null ? '-' : (Math.round(v * 10) / 10).toString().replace('.', ','));
const n0 = (v) => (v == null ? '-' : String(Math.round(v)));
const fLarga = (f) => `${Number(f.slice(8, 10))} de ${MESES[+f.slice(5, 7) - 1]} de ${f.slice(0, 4)}`;
const fCorta = (f) => `${DOW[new Date(f + 'T12:00:00Z').getUTCDay()]} ${f.slice(8, 10)}/${f.slice(5, 7)}`;
const limpiar = (s) => String(s || '').replace(/[^\x20-\x7E -ÿ–—…·]/g, '').replace(/\s+/g, ' ').trim();

/** Logo del cliente como dataURL PNG (vía canvas). null si no se puede (CORS, formato). */
export async function logoComoDataUrl(url) {
  if (!url) return null;
  try {
    const im = await new Promise((ok, mal) => { const i = new Image(); i.crossOrigin = 'anonymous'; i.onload = () => ok(i); i.onerror = mal; i.src = url; });
    const c = document.createElement('canvas');
    const k = Math.min(1, 600 / im.naturalWidth);
    c.width = Math.round(im.naturalWidth * k); c.height = Math.round(im.naturalHeight * k);
    c.getContext('2d').drawImage(im, 0, 0, c.width, c.height);
    return { data: c.toDataURL('image/png'), w: c.width, h: c.height };
  } catch (e) { return null; }
}

export function generarPdf(o) {
  const { obra, empresa, direccion, loc, desde, hasta, evals, resumen, ia, cruce, prom, U, logo, opciones = {} } = o;
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = 210, H = 297, M = 14;
  const dias = evals.filter((e) => e.estado);
  const hab = dias.filter((e) => e.laborable);
  const noAptos = hab.filter((e) => e.estado === 'no');
  const font = (size, style = 'normal', color = INK) => { doc.setFont('helvetica', style); doc.setFontSize(size); doc.setTextColor(...color); };
  const txt = (s, x, y, opt) => doc.text(limpiar(s), x, y, opt);

  const encabezado = (titulo) => {
    doc.setFillColor(...VERDE); doc.rect(0, 0, W, 3, 'F');
    font(8, 'bold', MUT); txt('INFORME DE CLIMA DE OBRA', M, 10);
    txt(limpiar(obra), W - M, 10, { align: 'right' });
    if (titulo) { font(15, 'bold'); txt(titulo, M, 20); }
  };

  // ───────── página 1: resumen
  doc.setFillColor(...VERDE); doc.rect(0, 0, W, 4, 'F');
  let y = 14;
  if (logo && opciones.logo !== false) {
    const k = Math.min(50 / logo.w, 16 / logo.h);
    try { doc.addImage(logo.data, 'PNG', M, y, logo.w * k, logo.h * k); } catch (e) { /* logo inválido */ }
  } else if (empresa) { font(13, 'bold'); txt(empresa, M, y + 8); }
  font(8, 'bold', MUT); txt('INFORME DE CLIMA DE OBRA', W - M, y + 4, { align: 'right' });
  font(8, 'normal', MUT); txt('Generado con LisualPro', W - M, y + 9, { align: 'right' });
  y += 26;
  font(22, 'bold'); txt(obra, M, y);
  y += 7;
  font(10, 'normal', MUT);
  if (direccion) { txt(direccion, M, y); y += 5; }
  txt(`Período: del ${fLarga(desde)} al ${fLarga(hasta)}`, M, y); y += 5;
  txt(`Ubicación: ${(+loc.lat).toFixed(4)}, ${(+loc.lng).toFixed(4)} · Horario de obra evaluado: ${U.jornadaDesde} a ${U.jornadaHasta} h`, M, y);
  y += 8;

  // tarjetas
  const kpis = [
    ['Días hábiles', n0(resumen.habiles), null], ['Aptos', n0(resumen.aptos), EST.apto], ['Con cuidado', n0(resumen.cuidado), EST.cuidado], ['No aptos', n0(resumen.noAptos), EST.no],
    ['Lluvia total', `${n0(resumen.mm)} mm`, null], ['Días de lluvia', n0(resumen.diasLluvia), null], ['Ráfaga máxima', `${n0(resumen.rafagaMax)} km/h`, null], ['Temp. máx / mín', `${n0(resumen.tmax)}° / ${n0(resumen.tmin)}°`, null],
  ];
  const bw = (W - 2 * M - 3 * 4) / 4, bh = 20;
  kpis.forEach(([l, v, e], i) => {
    const x = M + (i % 4) * (bw + 4), yy = y + Math.floor(i / 4) * (bh + 4);
    doc.setFillColor(...(e ? e.suave : CARD)); doc.roundedRect(x, yy, bw, bh, 3, 3, 'F');
    font(16, 'bold', e ? e.rgb : INK); txt(v, x + 4, yy + 10);
    font(8, 'normal', MUT); txt(l, x + 4, yy + 16);
  });
  y += 2 * (bh + 4) + 4;

  // recuadro para el comitente
  const lista = noAptos.slice(0, 12);
  const altoCaja = 30 + lista.length * 5.2 + (noAptos.length > lista.length ? 5 : 0);
  doc.setDrawColor(...EST.no.rgb); doc.setLineWidth(0.6); doc.setFillColor(255, 247, 248);
  doc.roundedRect(M, y, W - 2 * M, altoCaja, 3, 3, 'FD');
  font(12, 'bold', EST.no.rgb); txt(`${noAptos.length} ${noAptos.length === 1 ? 'día hábil no apto' : 'días hábiles no aptos'} por el clima`, M + 5, y + 8);
  font(9, 'normal', INK);
  const parrafo = doc.splitTextToSize(limpiar(noAptos.length
    ? `En estos días las condiciones climáticas impidieron trabajar normalmente en la obra (${resumen.noAptosLluvia} por lluvia o tormenta). Este informe, con datos meteorológicos de la ubicación de la obra y criterios explícitos, puede presentarse ante el comitente como respaldo para justificar demoras o solicitar una ampliación del plazo.`
    : 'En el período no hubo días hábiles en los que el clima impidiera trabajar según los criterios usados.'), W - 2 * M - 10);
  doc.text(parrafo, M + 5, y + 14);
  let ly = y + 14 + parrafo.length * 4.2 + 2;
  lista.forEach((e, i) => {
    const yy = ly + i * 5.2;
    font(8.5, 'bold'); txt(fCorta(e.fecha), M + 5, yy);
    font(8.5, 'normal', MUT);
    const causa = e.motivos.filter((m) => m.estado === 'no').map((m) => m.texto).join(' · ');
    txt(doc.splitTextToSize(limpiar(causa), W - 2 * M - 34)[0], M + 25, yy);
  });
  if (noAptos.length > lista.length) { font(8, 'italic', MUT); txt(`… y ${noAptos.length - lista.length} más (ver tabla diaria)`, M + 5, ly + lista.length * 5.2); }
  y += altoCaja + 6;

  // comparación y cruce con la obra
  const notas = [];
  if (prom) notas.push(`Promedio de los últimos ${prom.anios} años para ${MESES[+desde.slice(5, 7) - 1]} en este lugar: ${n1(prom.noAptoHabiles)} días hábiles no aptos y ${n0(prom.mm)} mm de lluvia. En este período: ${resumen.noAptos} días no aptos y ${n0(resumen.mm)} mm.`);
  if (cruce && opciones.cruce !== false) notas.push(cruce.menos != null && cruce.menos > 0
    ? `Actividad registrada por la cámara: los días de lluvia trabajó un ${cruce.menos} % menos de personal (promedio ${n1(cruce.personasLluvia)} contra ${n1(cruce.personasSeco)} personas en días secos).`
    : `Actividad registrada por la cámara: los días de lluvia la dotación fue similar a la de los días secos.`);
  notas.forEach((s) => {
    font(9, 'normal');
    const ls = doc.splitTextToSize(limpiar(s), W - 2 * M - 8);
    doc.setFillColor(...CARD); doc.roundedRect(M, y, W - 2 * M, ls.length * 4.4 + 5, 2, 2, 'F');
    font(9, 'normal'); doc.text(ls, M + 4, y + 5.5);
    y += ls.length * 4.4 + 8;
  });

  // ───────── página 2: gráficos
  doc.addPage();
  encabezado('Día por día');
  y = 28;
  const n = dias.length;
  const gx = M + 10, gw = W - 2 * M - 12;
  const slot = gw / Math.max(1, n);
  const etiquetas = (yy) => {
    const cada = Math.ceil(n / 16);
    font(6.5, 'normal', MUT);
    dias.forEach((e, i) => { if (i % cada === 0) txt(`${Number(e.fecha.slice(8))}/${Number(e.fecha.slice(5, 7))}`, gx + i * slot + slot / 2, yy, { align: 'center' }); });
  };
  const ejes = (yy, h, max, unidad, pasos = 4) => {
    doc.setDrawColor(...LINEA); doc.setLineWidth(0.2);
    for (let k = 0; k <= pasos; k++) {
      const ly2 = yy + h - (h * k) / pasos;
      doc.line(gx, ly2, gx + gw, ly2);
      font(6.5, 'normal', MUT); txt(`${n0((max * k) / pasos)}${unidad}`, gx - 1.5, ly2 + 1, { align: 'right' });
    }
  };
  // lluvia
  font(10, 'bold'); txt('Lluvia por día (mm)', M, y); y += 3;
  const maxMm = Math.max(5, ...dias.map((e) => e.datos.mmDia || 0));
  ejes(y, 38, maxMm, '');
  dias.forEach((e, i) => {
    const v = e.datos.mmDia || 0, h = (38 * v) / maxMm;
    if (v > 0) { doc.setFillColor(2, 132, 199); doc.rect(gx + i * slot + slot * 0.18, y + 38 - h, slot * 0.64, h, 'F'); }
  });
  // semáforo
  dias.forEach((e, i) => { doc.setFillColor(...EST[e.estado].rgb); doc.rect(gx + i * slot + 0.15, y + 40, Math.max(0.3, slot - 0.3), 2.2, 'F'); });
  etiquetas(y + 46);
  y += 54;
  // temperatura
  font(10, 'bold'); txt('Temperatura máxima y mínima (°C)', M, y); y += 3;
  const tvals = dias.flatMap((e) => [e.datos.tmaxDia ?? e.datos.tmax, e.datos.tminDia ?? e.datos.tmin]).filter((v) => v != null);
  const tlo = Math.floor(Math.min(...tvals) / 5) * 5, thi = Math.ceil(Math.max(...tvals) / 5) * 5 || 5;
  doc.setDrawColor(...LINEA); doc.setLineWidth(0.2);
  for (let k = 0; k <= 4; k++) { const ly2 = y + 38 - (38 * k) / 4; doc.line(gx, ly2, gx + gw, ly2); font(6.5, 'normal', MUT); txt(`${n0(tlo + ((thi - tlo) * k) / 4)}°`, gx - 1.5, ly2 + 1, { align: 'right' }); }
  const serie = (k, rgb) => {
    doc.setDrawColor(...rgb); doc.setLineWidth(0.6);
    let prev = null;
    dias.forEach((e, i) => {
      const v = k === 'max' ? e.datos.tmaxDia ?? e.datos.tmax : e.datos.tminDia ?? e.datos.tmin;
      if (v == null) { prev = null; return; }
      const p = [gx + i * slot + slot / 2, y + 38 - (38 * (v - tlo)) / Math.max(1, thi - tlo)];
      if (prev) doc.line(prev[0], prev[1], p[0], p[1]);
      prev = p;
    });
  };
  serie('max', [234, 88, 12]); serie('min', [37, 99, 235]);
  etiquetas(y + 43);
  font(7, 'normal', [234, 88, 12]); txt('Máxima', gx + gw - 30, y - 1); font(7, 'normal', [37, 99, 235]); txt('Mínima', gx + gw - 14, y - 1);
  y += 52;
  // viento
  font(10, 'bold'); txt('Ráfaga máxima del día (km/h)', M, y); y += 3;
  const maxR = Math.max(U.gruaRafaga + 10, ...dias.map((e) => e.datos.rafaga || 0));
  ejes(y, 30, maxR, '');
  dias.forEach((e, i) => {
    const v = e.datos.rafaga || 0, h = (30 * v) / maxR;
    doc.setFillColor(...(v >= U.rafagaNoApto ? EST.no.rgb : v >= U.rafagaCuidado ? EST.cuidado.rgb : [124, 58, 237])); doc.rect(gx + i * slot + slot * 0.25, y + 30 - h, slot * 0.5, h, 'F');
  });
  doc.setDrawColor(...EST.no.rgb); doc.setLineDashPattern([1.2, 1], 0); const yr = y + 30 - (30 * U.gruaRafaga) / maxR; doc.line(gx, yr, gx + gw, yr); doc.setLineDashPattern([], 0);
  font(6.5, 'normal', EST.no.rgb); txt(`límite para grúa ${U.gruaRafaga} km/h`, gx + gw, yr - 1, { align: 'right' });
  etiquetas(y + 35);
  y += 44;
  // personas
  if (ia && ia.size && opciones.cruce !== false) {
    font(10, 'bold'); txt('Personas en la obra (pico del día, cámara con IA)', M, y); y += 3;
    const maxP = Math.max(4, ...dias.map((e) => ia.get(e.fecha)?.pico || 0));
    ejes(y, 30, maxP, '');
    dias.forEach((e, i) => {
      const v = ia.get(e.fecha)?.pico || 0, h = (30 * v) / maxP;
      if (v > 0) { doc.setFillColor(...EST[e.estado].rgb); doc.rect(gx + i * slot + slot * 0.2, y + 30 - h, slot * 0.6, h, 'F'); }
    });
    etiquetas(y + 35);
    font(7, 'normal', MUT); txt('Color de cada barra = estado del clima ese día (verde apto, ámbar con cuidado, rojo no apto).', M, y + 40);
    y += 46;
  }
  // leyenda del semáforo
  font(7.5, 'normal', MUT);
  [['apto', 'Apto'], ['cuidado', 'Con cuidado'], ['no', 'No apto']].forEach(([k, t], i) => { doc.setFillColor(...EST[k].rgb); doc.rect(M + i * 30, y, 3, 3, 'F'); txt(t, M + i * 30 + 4.5, y + 2.6); });

  // ───────── tabla diaria
  if (opciones.tabla !== false) {
    doc.addPage();
    encabezado('Tabla diaria');
    const cols = [['Día', 20], ['Estado', 20], ['Lluvia obra', 17], ['Lluvia día', 17], ['Máx', 11], ['Mín', 11], ['Ráfaga', 13]];
    const conIa = ia && ia.size && opciones.cruce !== false;
    if (conIa) cols.push(['Pers.', 11]);
    const anchoMotivo = W - 2 * M - cols.reduce((s, c) => s + c[1], 0);
    cols.push(['Motivo', anchoMotivo]);
    const cab = (yy) => {
      doc.setFillColor(...CARD); doc.rect(M, yy - 4.2, W - 2 * M, 6.2, 'F');
      font(7.5, 'bold', MUT);
      let x = M + 1.5;
      cols.forEach(([t, w]) => { txt(t, x, yy); x += w; });
    };
    y = 30; cab(y); y += 6;
    dias.forEach((e) => {
      const motivo = e.estado === 'apto' ? '-' : e.motivos[0].texto + (e.motivos.length > 1 ? ` (+${e.motivos.length - 1})` : '');
      const lineas = doc.splitTextToSize(limpiar(motivo), anchoMotivo - 2);
      const alto = Math.max(5.2, lineas.length * 3.4 + 1.8);
      if (y + alto > H - 18) { doc.addPage(); encabezado('Tabla diaria (sigue)'); y = 30; cab(y); y += 6; }
      if (!e.laborable) { doc.setFillColor(250, 250, 250); doc.rect(M, y - 3.8, W - 2 * M, alto, 'F'); }
      const vals = [fCorta(e.fecha), EST[e.estado].txt, n1(e.datos.mmJ), n1(e.datos.mmDia), `${n0(e.datos.tmaxDia ?? e.datos.tmax)}°`, `${n0(e.datos.tminDia ?? e.datos.tmin)}°`, n0(e.datos.rafaga)];
      if (conIa) vals.push(ia.get(e.fecha) ? String(ia.get(e.fecha).pico) : '-');
      let x = M + 1.5;
      vals.forEach((v, i) => {
        if (i === 1) { doc.setFillColor(...EST[e.estado].suave); doc.roundedRect(x - 1, y - 3.2, cols[1][1] - 2, 4.4, 1, 1, 'F'); font(7.5, 'bold', EST[e.estado].rgb); } else font(7.5, i === 0 ? 'bold' : 'normal', e.laborable ? INK : MUT);
        txt(v, x, y);
        x += cols[i][1];
      });
      font(7.5, 'normal', e.laborable ? INK : MUT); doc.text(lineas, x, y);
      doc.setDrawColor(...LINEA); doc.setLineWidth(0.15); doc.line(M, y + alto - 3.8, W - M, y + alto - 3.8);
      y += alto;
    });
    font(7, 'italic', MUT); txt('Lluvia obra = mm caídos en el horario de trabajo. Días en gris: no laborables (no cuentan como hábiles).', M, y + 3);
  }

  // ───────── criterios, fuente y firma
  doc.addPage();
  encabezado('Criterios y fuente de los datos');
  y = 30;
  const crit = [
    `No apto: tormenta eléctrica en el horario de obra; lluvia de ${n1(U.lluviaNoApto)} mm o más entre las ${U.jornadaDesde} y las ${U.jornadaHasta} h; ráfagas de ${U.rafagaNoApto} km/h o más; temperatura de ${U.calorNoApto}° o más, o de ${U.frioNoApto}° o menos.`,
    `Con cuidado: lluvia de ${n1(U.lluviaCuidado)} mm o más o probabilidad de lluvia de ${U.probCuidado} % o más; más de 15 mm de lluvia fuera del horario (suelo con barro); ráfagas de ${U.rafagaCuidado} km/h o más; ${U.calorCuidado}° o más; ${U.frioCuidado}° o menos.`,
    `Días hábiles considerados: ${U.diasLaborales.map((d) => ['', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'][d]).join(', ')}.`,
    'Datos meteorológicos: Open-Meteo (open-meteo.com), reanálisis y modelos meteorológicos de alta resolución para las coordenadas de la obra, con valores hora por hora. Los días pasados corresponden a datos observados/reanalizados; no a pronósticos.',
    'La actividad de la obra (personas) surge del conteo automático de las fotos de la cámara Lisual instalada en la obra.',
  ];
  doc.setFillColor(...INK);
  crit.forEach((s) => { font(9.5, 'normal'); const ls = doc.splitTextToSize(limpiar(s), W - 2 * M - 6); doc.circle(M + 1.2, y - 1.1, 0.7, 'F'); doc.text(ls, M + 4, y); y += ls.length * 4.6 + 2.5; });
  y += 20;
  doc.setDrawColor(...INK); doc.setLineWidth(0.3);
  doc.line(M, y, M + 75, y); doc.line(W - M - 75, y, W - M, y);
  font(8.5, 'normal', MUT); txt('Firma y aclaración · Director de obra', M, y + 5); txt('Firma y aclaración · Comitente / Inspección', W - M - 75, y + 5);

  // pie en todas las páginas
  const total = doc.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    doc.setPage(p);
    doc.setDrawColor(...LINEA); doc.setLineWidth(0.3); doc.line(M, H - 12, W - M, H - 12);
    font(7.5, 'normal', MUT);
    txt(`${obra} · Informe de clima del ${fCorta(desde)} al ${fCorta(hasta)}`, M, H - 7);
    txt(`Página ${p} de ${total}`, W - M, H - 7, { align: 'right' });
  }
  const slug = String(obra || 'obra').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);
  return { doc, nombre: `clima-${slug}-${desde}_${hasta}.pdf` };
}
